/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { Box } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { EMPTY_DISPLAY } from "./cells";
import {
  panelCountStyles,
  panelHeadingStyles,
  revenueCenterCardStyles,
  revenueCenterFiguresStyles,
  revenueCenterNameStyles,
  revenueCenterNameTextStyles,
} from "./styles";

const PREVIEW_METRICS = ["net_sales", "covers", "void_qty"];

const previewFigures = (metrics = []) => {
  const byKey = new Map(metrics.map((metric) => [metric.metric, metric]));

  return PREVIEW_METRICS.map((key) => byKey.get(key))
    .filter(Boolean)
    .map((metric) => ({
      key: metric.metric,
      label: metric.label,
      display: metric?.today?.display ?? EMPTY_DISPLAY,
    }));
};

/**
 * The revenue-center list. Each card opens that center's own drill-in; the rail itself
 * never changes what the restaurant-level sections beside it are showing.
 *
 * Ordered by the API (net sales descending) and rendered as sent, including the
 * "Unknown revenue center" bucket, which is an ordinary row here — it only differs in
 * having no Toast configuration behind it.
 */
export const RevenueCenterRail = ({ centers = [], onSelect, testIdPrefix }) => (
  <Box data-testid={`${testIdPrefix}-revenue-centers`}>
    <Box sx={panelHeadingStyles}>
      <span>Revenue Centers</span>
      <Box component="span" sx={panelCountStyles} data-testid={`${testIdPrefix}-revenue-center-count`}>
        {centers.length}
      </Box>
    </Box>

    {centers.length === 0 ? (
      <Box
        sx={{ fontSize: "13px", color: "#71717a" }}
        data-testid={`${testIdPrefix}-revenue-centers-empty`}
      >
        No revenue centers traded on this day.
      </Box>
    ) : (
      centers.map((center) => (
        <Box
          key={center.guid}
          role="button"
          tabIndex={0}
          aria-label={`View ${center.name} details`}
          data-testid={`${testIdPrefix}-revenue-center-${center.guid}`}
          onClick={() => onSelect(center)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onSelect(center);
            }
          }}
          sx={revenueCenterCardStyles}
        >
          <Box sx={revenueCenterNameStyles}>
            <Box component="span" sx={revenueCenterNameTextStyles}>
              {center.name}
            </Box>
            <ChevronRightIcon sx={{ fontSize: "18px", color: "#a1a1aa", flexShrink: 0 }} />
          </Box>
          <Box sx={revenueCenterFiguresStyles}>
            {previewFigures(center.metrics).map((figure) => (
              <span key={figure.key}>{`${figure.label}: ${figure.display}`}</span>
            ))}
          </Box>
        </Box>
      ))
    )}
  </Box>
);
